// Module: useSequencingReachable
// -----------------------------------------------------------------
// Receives:  the signed-in user and acting teacher (useAuthStore)
// Produces:  whether the lesson sequencing page is worth linking to
// Owns:      one answer per viewer and the single in-flight request, so the
//            sidebar asks GET /lesson-sequencing at most once per viewer
// Does NOT:  persist across reloads, or gate the route (the page and the
//            backend stay the authority on access)
// -----------------------------------------------------------------
import { useEffect, useState } from "react";
import api from "@/utils/axios-utils";
import { useAuthStore } from "@/store/useAuthStore";
import { useLessonSequencingEnabled } from "@/hooks/useRuntimeFlags";

const answers = new Map<string, Promise<boolean>>();

// Auto-sequencing needs a curriculum bound to the organization to schedule
// from, and the overview is the only surface that knows. The entry point
// stays hidden until it says there is one, rather than leading to a page
// whose switch cannot be thrown.
//
// Unless sequencing is still switched on. A curriculum removed while it was
// on leaves the switch reading on with nothing behind it, and the page is
// where that is explained: it shows "No curriculum found" over a disabled
// switch. Hiding it would take the explanation away and leave a teacher
// whose groups silently stopped being sequenced with nothing to read.
function loadReachable(viewerKey: string): Promise<boolean> {
  const known = answers.get(viewerKey);
  if (known) return known;
  const request = api
    .get<{
      curriculum_available?: boolean;
      teacher_settings?: { enabled?: boolean };
    }>("/lesson-sequencing")
    .then(
      ({ data }) =>
        data?.curriculum_available === true ||
        data?.teacher_settings?.enabled === true,
    )
    .catch(() => {
      // A failed check is not an answer: let the next mount ask again.
      answers.delete(viewerKey);
      return false;
    });
  answers.set(viewerKey, request);
  return request;
}

/** Test seam: forget every viewer's answer. */
export function resetSequencingReachable() {
  answers.clear();
}

/**
 * Input:   isEnabled -- false skips the check (the viewer gets no link).
 * Output:  true once the overview says this viewer can use the page; false
 *          while unknown, so the link never appears and then vanishes.
 */
export function useSequencingReachable(isEnabled = true): boolean {
  const sequencingEnabled = useLessonSequencingEnabled();
  const userId = useAuthStore((s) => s.user?.id);
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const viewerKey =
    userId == null ? null : `${userId}:${actingTeacherId ?? ""}`;
  const [reachable, setReachable] = useState<{
    viewerKey: string;
    value: boolean;
  } | null>(null);

  useEffect(() => {
    if (!sequencingEnabled || !isEnabled || viewerKey === null) return;
    let active = true;
    void loadReachable(viewerKey).then((value) => {
      if (active) setReachable({ viewerKey, value });
    });
    return () => {
      active = false;
    };
  }, [sequencingEnabled, isEnabled, viewerKey]);

  return (
    sequencingEnabled &&
    isEnabled &&
    reachable?.viewerKey === viewerKey &&
    reachable.value
  );
}
